<template>
  <eg-modal
    id="custom-report-modal"
    size="large"
    show-backdrop
    persistent
    title=" "
  >
    <template #header>
      <div class="cr-modal__header">
        <div>
          <h2 class="cr-modal__title heading-s">
            {{ $t("metrics.createCustomReport") }}
          </h2>
          <p class="cr-modal__subtitle paragraph-s">
            {{ $t("metrics.selectMultipleAssignments") }}
          </p>
        </div>
        <button
          class="cr-modal__close-btn"
          @click="$egModal.hide('custom-report-modal')"
        >
          <eg-icon icon="close" />
        </button>
      </div>
    </template>

    <div class="cr-modal__body-wrap">
      <div class="cr-modal__filters">
        <eg-input
          :value="searchQuery"
          variant="search"
          :placeholder="$t('metrics.searchClassesOrAssignments')"
          class="cr-modal__search"
          @input="searchQuery = $event"
        />

        <metrics-multi-filter
          v-if="isOrgAdmin"
          :items="teacherItems"
          :all-label="$t('metrics.allTeachers')"
          :value="selectedTeachers"
          @input="selectedTeachers = $event"
        />

        <!-- <metrics-multi-filter
          :items="rubricItems"
          all-label="All Rubrics"
          :value="selectedRubricIds"
          @input="selectedRubricIds = $event"
        /> -->
      </div>

      <div class="cr-modal__panels">
        <div class="cr-modal__classes-panel">
          <div v-if="isLoading" class="cr-modal__state paragraph-s">
            {{ $t("metrics.loadingClasses") }}
          </div>
          <div
            v-else-if="filteredClasses.length === 0"
            class="cr-modal__state paragraph-s"
          >
            {{ $t("common.noClassesFound") }}
          </div>
          <div
            v-for="cls in filteredClasses"
            :key="cls.id"
            class="cr-modal__class-item"
            :class="{
              'cr-modal__class-item--active': activeClassId === cls.id,
            }"
            @click="setActiveClass(cls.id)"
          >
            <div class="cr-modal__class-info">
              <div class="cr-modal__class-name paragraph-s">{{ cls.name }}</div>
              <div class="cr-modal__class-teacher caption-s">
                {{ getTeacherName(cls.userId) }}
              </div>
            </div>
            <span
              v-if="selectedCountByClass(cls.id) > 0"
              class="cr-modal__class-badge"
            >
              {{ selectedCountByClass(cls.id) }}
            </span>
          </div>
        </div>

        <div class="cr-modal__assignments-panel">
          <template v-if="activeClass">
            <div class="cr-modal__assignments-header">
              <div class="d-flex flex-row flex-fill justify-content-between">
                <div>
                  <div class="cr-modal__assignments-title paragraph-s">
                    {{ activeClass.name }}
                  </div>
                  <div
                    class="cr-modal__class-teacher caption-s cr-modal__assignments-teacher"
                  >
                    {{ getTeacherName(activeClass.userId) }}
                  </div>
                </div>
              </div>
              <div class="d-flex flex-row flex-gap-1">
                <div class="cr-modal__assignments-meta">
                  <tag-item
                    :label="`${activeClassAssignments.length} ${$t(
                      'common.assignments'
                    )}`"
                    size="medium"
                  />
                  <tag-item
                    v-if="activeClassStudentsCount"
                    :label="
                      $t('metrics.studentsTag', {
                        count: activeClassStudentsCount,
                      })
                    "
                    size="medium"
                  />
                </div>
                <eg-button type="ordinary" size="md" @click="toggleSelectAll">
                  {{
                    isActiveClassFullySelected
                      ? $t("essays.deselect")
                      : $t("common.select")
                  }}
                </eg-button>
              </div>
            </div>

            <div
              v-if="activeClassAssignments.length === 0"
              class="cr-modal__state paragraph-s"
            >
              {{ $t("metrics.noAssignmentsFound") }}
            </div>

            <div class="cr-modal__assignments-list">
              <div
                v-for="asgn in activeClassAssignments"
                :key="asgn.id"
                class="cr-modal__assignment-item"
                :class="{
                  'cr-modal__assignment-item--checked':
                    checkedAssignmentIds.includes(asgn.id),
                }"
                @click="toggleAssignment(asgn.id)"
              >
                <eg-checkbox-input
                  :name="`asgn-${asgn.id}`"
                  :options="[{ label: '', value: asgn.id }]"
                  :value="checkedAssignmentIds"
                  size="large"
                  class="cr-modal__assignment-checkbox"
                  @click.native.stop
                  @change="onAssignmentChange(asgn.id, $event)"
                >
                  <template #content>
                    <div class="cr-modal__assignment-content">
                      <span class="cr-modal__assignment-name paragraph-s">{{
                        asgn.name
                      }}</span>
                      <div class="cr-modal__assignment-meta caption-s">
                        <template
                          v-if="
                            isLoadingDetails &&
                            !classDetailsCache[activeClassId]
                          "
                        >
                          <span class="cr-modal__assignment-meta-loading">{{
                            $t("common.loading")
                          }}</span>
                        </template>
                        <template v-else>
                          <span class="cr-modal__assignment-meta-score"
                            >{{ $t("metrics.tableAvgScore") }}:
                            {{
                              asgn.avgScore != null ? asgn.avgScore : "-"
                            }}</span
                          >
                          <span class="cr-modal__assignment-meta-dot">•</span>
                          <span class="cr-modal__assignment-meta-students"
                            >{{
                              asgn.essaysCount != null ? asgn.essaysCount : "-"
                            }}
                            {{ $t("metrics.students") }}</span
                          >
                          <template v-if="asgn.rubricName">
                            <span class="cr-modal__assignment-meta-dot">•</span>
                            <span class="cr-modal__assignment-meta-rubric">{{
                              asgn.rubricName
                            }}</span>
                          </template>
                        </template>
                      </div>
                    </div>
                  </template>
                </eg-checkbox-input>
              </div>
            </div>
          </template>

          <div v-else class="cr-modal__state paragraph-s">
            {{ $t("metrics.selectClassToSeeAssignments") }}
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer="{ close }">
      <div class="cr-modal__footer">
        <div class="cr-modal__footer-left">
          <span
            v-if="checkedAssignmentIds.length"
            class="cr-modal__selection-summary caption-s"
          >
            {{
              $t("metrics.selectionSummary", {
                classes: selectedClassIds.length,
                assignments: checkedAssignmentIds.length,
              })
            }}
          </span>
          <eg-button
            v-if="checkedAssignmentIds.length"
            type="ordinary"
            icon="close"
            icon-size="extra-small"
            class="clear-all"
            size="small"
            color="var(--color-text-neutral-secondary)"
            @click="clearAll"
          >
            {{ $t("common.clearAll") }}
          </eg-button>
        </div>
        <div class="cr-modal__footer-right">
          <eg-button type="secondary" @click="close">{{
            $t("common.cancel")
          }}</eg-button>
          <eg-button
            type="primary"
            :disabled="checkedAssignmentIds.length === 0"
            @click="onCreateReport"
          >
            {{ $t("metrics.createReport") }}
          </eg-button>
        </div>
      </div>
    </template>
  </eg-modal>
</template>

<script>
import { getRubrics } from "@/api/requests/rubric.js";
import { getFoldersByEntity } from "@/api/requests/folder.js";
import TagItem from "@/components/folders/tag-item.vue";
import MetricsMultiFilter from "./metrics_multi_filter.vue";

export default {
  name: "MetricsCustomReportModal",

  components: { TagItem, MetricsMultiFilter },

  props: {
    isOrgAdmin: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      searchQuery: "",
      selectedTeachers: [],
      selectedRubricIds: [],
      activeClassId: null,
      checkedAssignmentIds: [],
      rubrics: [],
      foldersData: {},
      isLoading: false,
      classDetailsCache: {},
      isLoadingDetails: false,
    };
  },

  computed: {
    classesAndAssignments() {
      const classes = [];
      const assignments = [];
      const platforms = ["essaygrader", "canvas", "google", "schoology"];
      platforms.forEach((platform) => {
        const platformFolders = this.foldersData[platform] || [];
        if (!platformFolders.length) return;
        const maxLevel = this._getTreeMaxLevel(platformFolders);
        this._extractClassesAndAssignments(
          platformFolders,
          classes,
          assignments,
          maxLevel
        );
      });
      return { classes, assignments };
    },

    allClasses() {
      return this.classesAndAssignments.classes;
    },

    allAssignments() {
      return this.classesAndAssignments.assignments;
    },

    filteredClasses() {
      let list = this.allClasses;
      if (this.selectedTeachers.length) {
        list = list.filter((c) => this.selectedTeachers.includes(c.userId));
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            this.assignmentsByClass(c.id).some((a) =>
              a.name.toLowerCase().includes(q)
            )
        );
      }
      return list;
    },

    activeClass() {
      if (!this.activeClassId) return null;
      return this.allClasses.find((c) => c.id === this.activeClassId) || null;
    },

    activeClassAssignments() {
      if (!this.activeClassId) return [];
      let list = this.assignmentsByClass(this.activeClassId);
      const details = this.classDetailsCache[this.activeClassId];
      if (details) {
        list = list.map((a) => {
          const detail = details.find((d) => d.id === a.id);
          return detail ? { ...a, ...detail } : a;
        });
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter((a) => a.name.toLowerCase().includes(q));
      }
      if (this.selectedRubricIds.length) {
        list = list.filter((a) => this.selectedRubricIds.includes(a.rubricId));
      }
      return list;
    },

    activeClassStudentsCount() {
      return this.activeClassAssignments.reduce(
        (sum, a) => sum + (a.essaysCount || 0),
        0
      );
    },

    isActiveClassFullySelected() {
      if (!this.activeClassAssignments.length) return false;
      return this.activeClassAssignments.every((a) =>
        this.checkedAssignmentIds.includes(a.id)
      );
    },

    rubricItems() {
      return this.rubrics.map((r) => ({ id: r.id, name: r.name }));
    },

    orgUsers() {
      return this.$store.state.orgUsers.users;
    },

    teacherItems() {
      const storeUser = this.$store.state.user;
      return this.orgUsers.map((t) => {
        const isCurrentUser = t.id === storeUser?.id;
        const firstName =
          t.firstName || (isCurrentUser ? storeUser?.firstName : "") || "";
        const lastName =
          t.lastName || (isCurrentUser ? storeUser?.lastName : "") || "";
        return { id: t.id, name: `${firstName} ${lastName}`.trim() };
      });
    },

    selectedClassIds() {
      const classIds = new Set();
      this.checkedAssignmentIds.forEach((assignmentId) => {
        const asgn = this.allAssignments.find((a) => a.id === assignmentId);
        if (asgn?.parentId) classIds.add(asgn.parentId);
      });
      return Array.from(classIds);
    },
  },

  mounted() {
    this.fetchFolders();
    this.fetchRubrics();
    if (this.isOrgAdmin) {
      this.$store.dispatch("orgUsers/load", this.entityId);
    }
  },

  methods: {
    _getTreeMaxLevel(folders) {
      if (!folders?.length) return 0;
      let max = 0;
      folders.forEach((f) => {
        max = Math.max(
          max,
          f.hierarchyLevel || 0,
          this._getTreeMaxLevel(f.children)
        );
      });
      return max;
    },

    _extractClassesAndAssignments(folders, classes, assignments, maxLevel) {
      folders.forEach((folder) => {
        if (
          (maxLevel === 3 && folder.hierarchyLevel === 2) ||
          (maxLevel === 2 && folder.hierarchyLevel === 1)
        ) {
          classes.push(folder);
        }
        if (
          (maxLevel === 3 && folder.hierarchyLevel === 3) ||
          (maxLevel === 2 && folder.hierarchyLevel === 2)
        ) {
          assignments.push(folder);
        }
        if (folder.children?.length) {
          this._extractClassesAndAssignments(
            folder.children,
            classes,
            assignments,
            maxLevel
          );
        }
      });
    },

    async fetchFolders() {
      this.isLoading = true;
      try {
        const data = await getFoldersByEntity(this.entityId);
        this.foldersData = data || {};
      } catch {
        this.foldersData = {};
      } finally {
        this.isLoading = false;
      }
    },

    getTeacherName(userId) {
      if (!userId) return "";

      // For org admins: look up in store users list
      const item = this.teacherItems.find((t) => t.id === userId);
      if (item) return item.name;

      // Fallback: check current user (covers non-admin case)
      const storeUser = this.$store.state.user;
      if (!storeUser) return "";
      if (userId === storeUser.id || userId === storeUser.clerkId) {
        return `${storeUser.firstName || ""} ${
          storeUser.lastName || ""
        }`.trim();
      }
      return "";
    },

    assignmentsByClass(classId) {
      return this.allAssignments.filter((a) => a.parentId === classId);
    },

    selectedCountByClass(classId) {
      const ids = this.assignmentsByClass(classId).map((a) => a.id);
      return ids.filter((id) => this.checkedAssignmentIds.includes(id)).length;
    },

    setActiveClass(classId) {
      this.activeClassId = classId;
      if (!this.classDetailsCache[classId]) {
        this.fetchClassDetails(classId);
      }
    },

    async fetchClassDetails(classId) {
      this.isLoadingDetails = true;
      try {
        const data = await getFoldersByEntity(this.entityId, classId, {
          childrenDetails: true,
        });
        const children = data?.children || [];
        this.$set(this.classDetailsCache, classId, children);
      } catch {
        this.$set(this.classDetailsCache, classId, []);
      } finally {
        this.isLoadingDetails = false;
      }
    },

    toggleAssignment(id) {
      if (this.checkedAssignmentIds.includes(id)) {
        this.checkedAssignmentIds = this.checkedAssignmentIds.filter(
          (i) => i !== id
        );
      } else {
        this.checkedAssignmentIds.push(id);
      }
    },

    onAssignmentChange(assignmentId, newValues) {
      if (newValues.length > 0) {
        if (!this.checkedAssignmentIds.includes(assignmentId)) {
          this.checkedAssignmentIds.push(assignmentId);
        }
      } else {
        this.checkedAssignmentIds = this.checkedAssignmentIds.filter(
          (id) => id !== assignmentId
        );
      }
    },

    toggleSelectAll() {
      if (this.isActiveClassFullySelected) {
        const ids = this.activeClassAssignments.map((a) => a.id);
        this.checkedAssignmentIds = this.checkedAssignmentIds.filter(
          (id) => !ids.includes(id)
        );
      } else {
        const ids = this.activeClassAssignments.map((a) => a.id);
        ids.forEach((id) => {
          if (!this.checkedAssignmentIds.includes(id)) {
            this.checkedAssignmentIds.push(id);
          }
        });
      }
    },

    onCreateReport() {
      const selectedClasses = this.selectedClassIds
        .map((classId) => {
          const classObj = this.allClasses.find((c) => c.id === classId);
          if (!classObj) {
            return null;
          }
          const assignments = this.checkedAssignmentIds
            .map((assignmentId) =>
              this.allAssignments.find(
                (assignment) => assignment.id === assignmentId
              )
            )
            .filter((assignment) => assignment?.parentId === classId);
          return {
            id: classObj.id,
            name: classObj.name,
            assignments: assignments.map(({ id, name, parentId }) => ({
              id,
              name,
              parentId,
            })),
          };
        })
        .filter(Boolean);
      this.$emit("confirm", {
        selectedClasses,
        teacherNames: this._getSelectedTeacherNames(),
        assignmentNames: this._getSelectedAssignmentNames(),
      });
      this.$egModal.hide("custom-report-modal");
    },

    _getSelectedTeacherNames() {
      const userIds = new Set();
      this.selectedClassIds.forEach((classId) => {
        const cls = this.allClasses.find((c) => c.id === classId);
        if (cls?.userId) userIds.add(cls.userId);
      });
      return Array.from(userIds)
        .map((userId) => this.getTeacherName(userId))
        .filter(Boolean);
    },

    _getSelectedAssignmentNames() {
      return this.checkedAssignmentIds
        .map((id) => {
          const assignment = this.allAssignments.find((assn) => assn.id === id);
          return assignment?.name || null;
        })
        .filter(Boolean);
    },

    async fetchRubrics() {
      try {
        const result = await getRubrics();
        this.rubrics = result?.data || [];
      } catch {
        this.rubrics = [];
      }
    },

    clearAll() {
      this.checkedAssignmentIds = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.cr-modal {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  }

  &__title {
    margin: 0 0 6px;
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__subtitle {
    margin: 0;
    color: var(--color-text-neutral-secondary, #566370);
  }

  &__close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-icon-neutral-secondary, #8a97a4);
    flex-shrink: 0;
    font-size: 18px;

    &:hover {
      color: var(--color-text-neutral-on-white, #1a2330);
    }
  }

  &__body-wrap {
    display: flex;
    flex-direction: column;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    flex-wrap: wrap;
  }

  &__search {
    flex: 1;
    min-width: 200px;
    max-width: 305px;
  }

  &__panels {
    display: grid;
    grid-template-columns: 260px 1fr;
    border-top: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    height: 420px;
    overflow: hidden;
  }

  &__classes-panel {
    border-right: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    overflow-y: auto;
    height: 100%;
  }

  &__class-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-left: 4px solid transparent;
    cursor: pointer;
    &:hover {
      background: var(--color-surface-neutral-hover, #f9fafb);
    }

    &--active {
      background: var(--color-surface-primary-light-1, #f6f2ff);
      border-left: 4px solid var(--color-stroke-primary-default, #8a3ffc);
      border-bottom: 1px solid var(--color-stroke-neutral-default);
      background: var(--color-surface-primary-light-1);
      .cr-modal__class-name {
        color: var(--color-text-primary-default);
      }
    }
  }

  &__class-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__class-name {
    color: var(--color-text-neutral-on-white, #1a2330);
    font-weight: var(--typography-font-weight-fw-600);
    line-height: 1.4;
  }

  &__class-teacher {
    color: var(--color-text-neutral-secondary, #566370);
  }

  &__class-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: var(--color-surface-primary-default, #7c3aed);
    color: #fff;
    font-size: var(--typography-font-size-fs-100);
    font-weight: var(--typography-font-weight-fw-700, 700);
    flex-shrink: 0;
  }

  &__assignments-panel {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
  }

  &__assignments-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    position: sticky;
    top: 0;
    background: var(--color-surface-neutral-background, #fff);
    z-index: 1;
  }

  &__assignments-title {
    color: var(--color-text-neutral-on-white, #1a2330);
    margin: 0 0 4px;
  }

  &__assignments-teacher {
    margin: 2px 0 6px;
  }

  &__assignments-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__assignments-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
  }

  &__assignment-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 16px;
    border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-radius: 8px;
    background: var(--color-surface-neutral-background, #fff);
    transition: border-color 0.15s;
    cursor: pointer;
    &:hover {
      border-color: var(--color-stroke-primary-default, #8a3ffc);
    }

    &--checked {
      border-radius: var(--radius-rd-300, 8px);
      border: 1px solid var(--color-stroke-neutral-default);
      background: var(--color-surface-primary-light-1);
    }
  }

  &__assignment-checkbox {
    ::v-deep .eg-radio-checkbox-input__label {
      width: 100%;
      cursor: pointer;
      align-items: center;
      gap: 12px;
      margin-bottom: 0;
    }
  }

  &__assignment-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__assignment-name {
    font-weight: var(--typography-font-weight-fw-500, 500);
    color: var(--color-text-neutral-on-white, #1a2330);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__assignment-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--color-text-neutral-fade);
  }

  &__assignment-meta-score {
    flex-shrink: 0;
  }

  &__assignment-meta-students {
    flex-shrink: 0;
  }

  &__assignment-meta-dot {
    color: var(--color-text-neutral-fade);
    flex-shrink: 0;
    margin: 0 2px;
  }

  &__assignment-meta-rubric {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__assignment-meta-loading {
    font-style: italic;
  }

  &__state {
    padding: 24px 20px;
    color: var(--color-text-neutral-secondary, #566370);
    text-align: center;
  }

  &__footer {
    display: flex;
    align-items: center;
    flex: 1;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 24px;
  }

  &__footer-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__footer-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__selection-summary {
    color: var(--color-text-neutral-secondary, #566370);
    font-weight: var(--typography-font-weight-fw-500, 500);
    white-space: nowrap;
  }
}
</style>
<style lang="scss">
#custom-report-modal {
  .eg-modal__body {
    padding: 0;
  }
  .clear-all {
    padding: 4px 6px;
    border-radius: var(--radius-rd-500, 16px);
    border: 1px solid var(--color-stroke-neutral-disabled);
    .eg-icon {
      font-size: var(--typography-font-size-fs-200);
    }
  }
  .cr-modal__footer {
    padding: 0 8px;
  }
}
</style>
